import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Maximize, Minimize } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0b01affe-3e55-4e38-af85-fb6ae72b034b/artifacts/aji67e46_CAKE%20FACTORY_00_LOGO_600px.png";

// Cake images to replace the party text
const CAKE_IMAGES = [
  "https://images.unsplash.com/photo-1622576890453-8e50b6f7d5b0?w=300&q=80",
  "https://images.pexels.com/photos/2337821/pexels-photo-2337821.jpeg?auto=compress&w=300",
  "https://images.pexels.com/photos/4699096/pexels-photo-4699096.jpeg?auto=compress&w=300",
];

const ROUND_NAMES = ["Test", "Round 1", "Round 2", "Round 3", "Round 4"];

const PERMANENT_UPGRADES = ["Double Tray", "Extra Layers", "Cake Storage", "Swap Tables", "4th Order Slot", "4th Delivery Slot"];

const UPGRADES = [
  { name: "Strategy Break", short: "Strategy" },
  { name: "Birthday Candle", short: "Candle" },
  { name: "Double Tray", short: "Dbl Tray" },
  { name: "Extra Layers", short: "Layers" },
  { name: "Cake Storage", short: "Storage" },
  { name: "Swap Tables", short: "Swap" },
  { name: "Last Call", short: "Last Call" },
  { name: "4th Order Slot", short: "4th Order" },
  { name: "4th Delivery Slot", short: "4th Deliv" },
];

function AnimatedNumber({ value }) {
  // Directly display the value - no animation lag
  return <span>€{value}</span>;
}

// Dynamic team card component with size based on team count
function TeamCard({ team, isLeader, teamCount, currentRound }) {
  // Calculate card size class based on team count
  const getCardSizeClass = () => {
    if (teamCount === 1) return "w-full max-w-4xl mx-auto";
    if (teamCount === 2) return "w-full";
    if (teamCount === 3) return "w-full";
    if (teamCount <= 4) return "w-full";
    return "w-full";
  };

  const getScoreSize = () => {
    if (teamCount === 1) return "text-9xl";
    if (teamCount === 2) return "text-7xl";
    if (teamCount === 3) return "text-6xl";
    if (teamCount <= 5) return "text-5xl";
    return "text-4xl";
  };

  const getNameSize = () => {
    if (teamCount === 1) return "text-5xl";
    if (teamCount === 2) return "text-4xl";
    if (teamCount === 3) return "text-3xl";
    return "text-2xl";
  };

  const showLeaderHighlight = isLeader && team.total_score > 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className={`glass-card p-6 lg:p-8 ${getCardSizeClass()} ${
        showLeaderHighlight ? "ring-4 ring-[#F59E0B] shadow-[0_0_30px_rgba(245,158,11,0.4)]" : ""
      }`}
      data-testid={`team-card-${team.id}`}
    >
      {/* Team Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            {isLeader && team.total_score > 0 && (
              <span className={teamCount <= 2 ? "text-5xl" : "text-3xl"}>👑</span>
            )}
            <h2 className={`font-heading ${getNameSize()} text-white`}>
              {team.name}
            </h2>
          </div>
        </div>
      </div>

      {/* Total Score */}
      <div
        className={`font-mono ${getScoreSize()} font-bold mb-6 ${
          showLeaderHighlight ? "text-[#F59E0B]" : "text-white"
        }`}
        data-testid={`team-total-${team.id}`}
      >
        <AnimatedNumber value={team.total_score} />
      </div>

      {/* Round Scores */}
      <div className={`grid grid-cols-5 gap-2 mb-6 text-center ${teamCount <= 2 ? "text-lg" : ""}`}>
        {ROUND_NAMES.map((name, idx) => (
          <div
            key={name}
            className={`rounded-lg py-2 ${teamCount <= 2 ? "py-3" : ""} ${
              idx === currentRound
                ? "bg-[#D946EF]/30 text-white"
                : "bg-white/5 text-white/50"
            }`}
          >
            <div className={`${teamCount <= 2 ? "text-sm" : "text-[10px]"} opacity-70`}>{name}</div>
            <div className={`font-mono font-semibold ${teamCount <= 2 ? "text-xl" : ""}`}>€{team.scores[idx]}</div>
          </div>
        ))}
      </div>

      {/* Upgrades */}
      <div className={`flex flex-wrap gap-2 ${teamCount <= 2 ? "gap-3" : ""}`}>
        {UPGRADES.map((upgrade) => {
          const teamUpgrade = team.upgrades.find(
            (u) => u.name === upgrade.name
          );
          const isPurchased = teamUpgrade?.purchased;
          const isPermanent = PERMANENT_UPGRADES.includes(upgrade.name);

          return (
            <span
              key={upgrade.name}
              className={`upgrade-pill ${isPurchased ? "active" : "inactive"} ${
                teamCount <= 2 ? "text-base px-4 py-2" : ""
              } ${isPurchased && isPermanent ? "ring-1 ring-green-400" : ""}`}
              data-testid={`upgrade-pill-${team.id}-${upgrade.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {isPermanent && isPurchased && "🔒 "}
              {upgrade.short}
            </span>
          );
        })}
      </div>
    </motion.div>
  );
}

export default function TeamDisplay() {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(420);
  const [timerRunning, setTimerRunning] = useState(false);
  // Meeting state
  const [meetingActive, setMeetingActive] = useState(false);
  const [meetingTimerSeconds, setMeetingTimerSeconds] = useState(600);
  const [meetingTimerRunning, setMeetingTimerRunning] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const prevRoundRef = useRef(0);
  const audioRef = useRef(null);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Listen for fullscreen changes (e.g. ESC key)
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Keyboard shortcut: F to toggle fullscreen
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "f" || e.key === "F") toggleFullscreen();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleFullscreen]);

  // Format timer as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Fetch game data
  const fetchGame = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}`);
      const newGame = response.data;
      
      // Check for round change
      if (prevRoundRef.current !== null && newGame.current_round !== prevRoundRef.current) {
        // Trigger confetti on round change
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ["#D946EF", "#EC4899", "#F59E0B", "#22C55E"],
        });
      }
      prevRoundRef.current = newGame.current_round;
      
      // Always update game state with fresh data
      setGame(newGame);
      
      // Always sync timer state from backend
      setTimerRunning(newGame.timer_running || false);
      setTimerSeconds(newGame.timer_seconds ?? 420);
      
      // Sync meeting state
      setMeetingActive(newGame.meeting_active || false);
      setMeetingTimerRunning(newGame.meeting_timer_running || false);
      setMeetingTimerSeconds(newGame.meeting_timer_seconds ?? 600);
      
      if (loading) setLoading(false);
    } catch (err) {
      if (loading) {
        setError(true);
        setLoading(false);
      }
    }
  }, [gameId, loading]);

  // Initial fetch and polling - faster interval for timer sync
  useEffect(() => {
    fetchGame();
    const interval = setInterval(fetchGame, 1000); // Poll every 1 second for timer sync
    return () => clearInterval(interval);
  }, [fetchGame]);

  // Play alarm when timer hits 0
  useEffect(() => {
    if (timerSeconds === 0 && timerRunning === false) {
      // Play alarm sound
      if (audioRef.current) {
        audioRef.current.play().catch(() => {});
      }
      // Trigger celebration confetti
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 },
        colors: ["#D946EF", "#EC4899", "#F59E0B"],
      });
    }
  }, [timerSeconds, timerRunning]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#2E1065] flex items-center justify-center">
        <div className="text-center">
          <img src={LOGO_URL} alt="Cake Factory" className="w-32 h-32 mx-auto mb-4 animate-pulse" />
          <p className="text-white/70 font-medium text-xl">Loading game...</p>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-[#2E1065] flex items-center justify-center">
        <div className="text-center">
          <img src={LOGO_URL} alt="Cake Factory" className="w-32 h-32 mx-auto mb-4 opacity-50" />
          <p className="text-white/70 font-medium text-xl">Game not found</p>
        </div>
      </div>
    );
  }

  // Keep teams in fixed creation order (Team 1, Team 2, ...); no reordering by score
  const orderedTeams = game.teams;
  const teamCount = game.teams.length;
  // Leader = team with highest total_score (first one wins ties)
  const leaderId = game.teams.reduce(
    (best, t) => (t.total_score > (best?.total_score ?? -Infinity) ? t : best),
    null,
  )?.id;

  // Dynamic grid classes based on team count
  const getGridClass = () => {
    if (teamCount === 1) return "flex justify-center items-center";
    if (teamCount === 2) return "grid grid-cols-2 gap-8";
    if (teamCount === 3) return "grid grid-cols-3 gap-6";
    if (teamCount === 4) return "grid grid-cols-2 lg:grid-cols-4 gap-6";
    if (teamCount <= 6) return "grid grid-cols-2 lg:grid-cols-3 gap-5";
    if (teamCount <= 8) return "grid grid-cols-2 lg:grid-cols-4 gap-4";
    return "grid grid-cols-2 lg:grid-cols-5 gap-4";
  };

  return (
    <div className="min-h-screen bg-[#2E1065] relative overflow-hidden">
      {/* Audio for alarm */}
      <audio
        ref={audioRef}
        src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"
        preload="auto"
      />

      {/* Cake decorations instead of party text */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Top left cake */}
        <img 
          src={CAKE_IMAGES[0]} 
          alt="" 
          className="absolute -top-10 -left-10 w-48 h-48 object-cover rounded-full opacity-20 blur-sm"
        />
        {/* Top right cake */}
        <img 
          src={CAKE_IMAGES[1]} 
          alt="" 
          className="absolute -top-5 -right-10 w-56 h-56 object-cover rounded-full opacity-20 blur-sm"
        />
        {/* Bottom left cake */}
        <img 
          src={CAKE_IMAGES[2]} 
          alt="" 
          className="absolute -bottom-10 -left-5 w-52 h-52 object-cover rounded-full opacity-20 blur-sm"
        />
        {/* Bottom right cake */}
        <img 
          src={CAKE_IMAGES[0]} 
          alt="" 
          className="absolute -bottom-5 -right-10 w-44 h-44 object-cover rounded-full opacity-20 blur-sm"
        />
        {/* Center bottom cake (larger) */}
        <img 
          src={CAKE_IMAGES[1]} 
          alt="" 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-64 object-cover rounded-full opacity-15 blur-sm"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col p-6 lg:p-10">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img
              src={LOGO_URL}
              alt="Cake Factory"
              className="w-20 h-20 lg:w-28 lg:h-28 drop-shadow-2xl"
              data-testid="display-logo"
            />
            <div>
              <h1 className="font-heading text-3xl lg:text-5xl text-white glow-text">
                Cake Factory
              </h1>
              <p className={`font-medium text-lg lg:text-xl ${meetingActive ? "text-[#22C55E]" : "text-white/60"}`}>
                {meetingActive ? "Staff Meeting" : ROUND_NAMES[game.current_round]}
              </p>
            </div>
          </div>

          {/* Timer - Show meeting timer if meeting is active */}
          <div className="text-right flex items-start gap-3">
            <div>
              <div
                className={`font-mono text-6xl lg:text-8xl font-bold ${
                  meetingActive
                    ? meetingTimerSeconds < 60 ? "timer-warning text-red-500" : "text-[#22C55E]"
                    : timerSeconds < 60 ? "timer-warning text-red-500" : "text-white glow-text"
                }`}
                data-testid="display-timer"
              >
                {meetingActive ? formatTime(meetingTimerSeconds) : formatTime(timerSeconds)}
              </div>
              {meetingActive && (
                <div className="text-[#22C55E] text-xl font-heading mt-1 animate-pulse">
                  UPGRADES ENABLED
                </div>
              )}
            </div>
            <button
              onClick={toggleFullscreen}
              className="w-11 h-11 lg:w-12 lg:h-12 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center transition-colors text-white shrink-0"
              data-testid="display-fullscreen-btn"
              title={isFullscreen ? "Exit fullscreen (F)" : "Fullscreen (F)"}
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Teams Grid - Dynamic sizing */}
        <main className="flex-1 flex items-center">
          <div className={`w-full ${getGridClass()}`}>
            <AnimatePresence>
              {orderedTeams.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  isLeader={team.id === leaderId}
                  teamCount={teamCount}
                  currentRound={game.current_round}
                />
              ))}
            </AnimatePresence>
          </div>
        </main>

        {/* Footer */}
        <footer className="mt-8 text-center">
          <p className="text-white/30 text-sm">
            Game ID: {gameId}
          </p>
        </footer>
      </div>
    </div>
  );
}
